import { useState, type FormEvent } from "react";
import type { AdminAccount, TemporaryPassword } from "../../api/admin";
import type { UserRole } from "../../api/types";
import { Avatar, Badge } from "../../components/admin/AdminUi";
import { DataTable, type Column } from "../../components/admin/DataTable";
import { Banner } from "../../components/Banner";
import { Field, inputShell } from "../../components/Field";
import { compactDate } from "../../lib/format";

export const ADMIN_ROLE_LABEL: Record<Exclude<UserRole, "Participant">, string> = {
  SuperAdmin: "Super admin",
  GigAdmin: "Gig team",
  FinanceAdmin: "Finance team",
};

export const COMPANY_DOMAIN = "cchub.africa";

export const isCompanyEmail = (email: string) =>
  email.trim().toLowerCase().endsWith(`@${COMPANY_DOMAIN}`);

interface TeamScreenProps {
  admins: AdminAccount[];
  currentUserId: string;
  canManage: boolean;
  busy: boolean;
  error: string | null;
  adding: boolean;
  issued: { password: TemporaryPassword; reason: "added" | "reset" } | null;
  onAdding: (open: boolean) => void;
  onInvite: (input: { email: string; firstName: string; lastName: string; role: UserRole }) => void;
  onReset: (admin: AdminAccount) => void;
  onDismissIssued: () => void;
}

/** The admin team. Super admins add people and reset a forgotten password; everyone can see who has access. */
export function TeamScreen(props: TeamScreenProps) {
  const { admins, currentUserId, canManage, onReset } = props;

  const columns: Column<AdminAccount>[] = [
    {
      key: "name",
      header: "Admin",
      sortValue: (a) => `${a.firstName} ${a.lastName}`.toLowerCase(),
      render: (a) => (
        <div className="flex items-center gap-3">
          <Avatar first={a.firstName} last={a.lastName} />
          <div className="min-w-0">
            <div className="font-semibold">
              {a.firstName} {a.lastName}
              {a.id === currentUserId ? <span className="text-text-muted"> (you)</span> : null}
            </div>
            <div className="truncate text-[12px] text-text-muted">{a.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: "role",
      header: "Role",
      sortValue: (a) => a.role,
      render: (a) => (
        <Badge tone={a.role === "SuperAdmin" ? "accent" : "neutral"}>
          {ADMIN_ROLE_LABEL[a.role as keyof typeof ADMIN_ROLE_LABEL]}
        </Badge>
      ),
    },
    {
      key: "status",
      header: "Status",
      hideBelow: "sm",
      render: (a) =>
        a.mustChangePassword ? (
          <Badge tone="warning">Must set password</Badge>
        ) : (
          <Badge tone="positive">Active</Badge>
        ),
    },
    {
      key: "lastLogin",
      header: "Last sign-in",
      hideBelow: "md",
      sortValue: (a) => a.lastLogin ?? "",
      render: (a) => (
        <span className="text-text-muted">{a.lastLogin ? compactDate(a.lastLogin) : "Never"}</span>
      ),
    },
    ...(canManage
      ? [
          {
            key: "actions",
            header: "",
            align: "right" as const,
            render: (a: AdminAccount) =>
              a.id === currentUserId ? null : (
                <button
                  type="button"
                  aria-label={`Reset password for ${a.email}`}
                  onClick={() => onReset(a)}
                  className="rounded-[9px] border border-border px-3 py-1.5 text-[12.5px] font-semibold text-text-muted hover:border-accent hover:text-accent"
                >
                  Reset password
                </button>
              ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-[13.5px] text-text-muted">
        Admin accounts use a company email (@{COMPANY_DOMAIN}).{" "}
        {canManage
          ? "Anyone who forgets their password can reset it from the sign-in page; use Reset password here if their email isn't working."
          : "Forgot your password? Use “Forgot password?” on the sign-in page, or ask a super admin."}
      </p>
      {props.error && !props.adding ? <Banner tone="bad">{props.error}</Banner> : null}
      <DataTable<AdminAccount>
        rows={admins}
        columns={columns}
        rowKey={(a) => a.id}
        empty="No admins yet."
      />
      {props.adding ? <AddAdminDrawer {...props} /> : null}
      {props.issued ? <IssuedPassword {...props} issued={props.issued} /> : null}
    </div>
  );
}

function AddAdminDrawer({ busy, error, onAdding, onInvite }: TeamScreenProps) {
  const [draft, setDraft] = useState({
    email: "",
    firstName: "",
    lastName: "",
    role: "GigAdmin" as UserRole,
  });
  const set = (patch: Partial<typeof draft>) => setDraft({ ...draft, ...patch });
  const emailProblem =
    draft.email.includes("@") && !isCompanyEmail(draft.email)
      ? `Use a company email ending in @${COMPANY_DOMAIN}.`
      : null;
  const ready =
    isCompanyEmail(draft.email) &&
    Boolean(draft.firstName.trim()) &&
    Boolean(draft.lastName.trim());

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) onInvite({ ...draft, email: draft.email.trim() });
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-[#231416]/30"
        aria-hidden="true"
        onClick={() => onAdding(false)}
      />
      <form
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-label="Add an admin"
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[460px] flex-col bg-surface shadow-[-12px_0_40px_rgba(35,20,22,0.18)]"
      >
        <div className="flex items-center justify-between border-b border-border p-5">
          <b className="text-[17px]">Add an admin</b>
          <button
            type="button"
            aria-label="Close"
            onClick={() => onAdding(false)}
            className="h-9 w-9 rounded-[9px] text-text-muted hover:bg-surface-high"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <Field label="Company email" htmlFor="ad-email" error={emailProblem}>
            <input
              id="ad-email"
              type="email"
              placeholder={`name@${COMPANY_DOMAIN}`}
              className={inputShell}
              value={draft.email}
              aria-describedby="ad-email-error"
              onChange={(e) => set({ email: e.target.value })}
            />
          </Field>
          <Field label="First name" htmlFor="ad-first">
            <input
              id="ad-first"
              className={inputShell}
              value={draft.firstName}
              onChange={(e) => set({ firstName: e.target.value })}
            />
          </Field>
          <Field label="Last name" htmlFor="ad-last">
            <input
              id="ad-last"
              className={inputShell}
              value={draft.lastName}
              onChange={(e) => set({ lastName: e.target.value })}
            />
          </Field>
          <Field label="Role" htmlFor="ad-role">
            <select
              id="ad-role"
              className={inputShell}
              value={draft.role}
              onChange={(e) => set({ role: e.target.value as UserRole })}
            >
              {Object.entries(ADMIN_ROLE_LABEL).map(([role, label]) => (
                <option key={role} value={role}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <p className="m-0 text-[13px] text-text-muted">
            You&apos;ll get a one-time password to give them. They&apos;ll choose their own the
            first time they sign in.
          </p>
          {error ? <Banner tone="bad">{error}</Banner> : null}
        </div>
        <div className="flex gap-2 border-t border-border p-4">
          <button
            type="submit"
            disabled={busy || !ready}
            className="h-10 flex-1 rounded-[10px] bg-accent text-[14px] font-bold text-on-accent disabled:opacity-50"
          >
            {busy ? "Adding..." : "Add admin"}
          </button>
          <button
            type="button"
            onClick={() => onAdding(false)}
            className="h-10 rounded-[10px] border border-border px-4 text-[14px] font-semibold"
          >
            Cancel
          </button>
        </div>
      </form>
    </>
  );
}

/** Shown once. The server never returns it again, so the super admin copies it now and passes it on privately. */
function IssuedPassword({
  issued,
  onDismissIssued,
}: TeamScreenProps & { issued: NonNullable<TeamScreenProps["issued"]> }) {
  const [copied, setCopied] = useState(false);
  const { user, temporaryPassword } = issued.password;
  return (
    <>
      <div className="fixed inset-0 z-40 bg-[#231416]/40" aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="One-time password"
        className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-32px)] max-w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-[16px] bg-surface p-6 shadow-[0_20px_60px_rgba(35,20,22,0.25)]"
      >
        <b className="block text-[17px]">
          {issued.reason === "added" ? `${user.firstName} can now sign in` : "Password reset"}
        </b>
        <p className="mb-3 mt-1 text-[13.5px] text-text-muted">
          Give {user.email} this one-time password privately (not in a group chat). It won&apos;t be
          shown again. They&apos;ll choose their own when they sign in.
        </p>
        <code className="block select-all rounded-[10px] bg-surface-high px-4 py-3 text-center text-[19px] font-bold tracking-[0.06em]">
          {temporaryPassword}
        </code>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard?.writeText(temporaryPassword).then(() => setCopied(true));
            }}
            className="h-10 flex-1 rounded-[10px] border border-border text-[14px] font-semibold"
          >
            {copied ? "Copied" : "Copy"}
          </button>
          <button
            type="button"
            onClick={onDismissIssued}
            className="h-10 flex-1 rounded-[10px] bg-accent text-[14px] font-bold text-on-accent"
          >
            Done
          </button>
        </div>
      </div>
    </>
  );
}
